import { useState, useEffect } from 'react'

interface OnboardingProps {
  onComplete: () => void
}

const ONBOARDING_KEY = 'reframer_onboarding_complete'

export function useOnboarding() {
  const [showOnboarding, setShowOnboarding] = useState(false)

  useEffect(() => {
    // Check if user has completed onboarding
    const completed = localStorage.getItem(ONBOARDING_KEY)
    if (!completed) {
      setShowOnboarding(true)
    }
  }, [])

  const completeOnboarding = () => {
    localStorage.setItem(ONBOARDING_KEY, 'true')
    setShowOnboarding(false)
  }

  return { showOnboarding, completeOnboarding }
}

export function Onboarding({ onComplete }: OnboardingProps) {
  const [step, setStep] = useState(0)

  const steps = [
    {
      title: 'Welcome to PresentLens',
      content: 'A private space to process difficult thoughts and feelings using evidence-based cognitive techniques.',
      tip: 'Everything stays on your device. Your thoughts are yours alone.',
    },
    {
      title: 'How It Works',
      content: 'Speak or type what\'s on your mind. Our AI helps you identify thinking patterns and find a more balanced perspective.',
      tip: 'This is not therapy—it\'s a tool to support your wellbeing between sessions.',
    },
    {
      title: 'The Reframe Process',
      content: '1. Share what happened and how you feel\n2. Review the AI\'s analysis\n3. Explore evidence for and against your thoughts\n4. Discover a balanced perspective',
      tip: 'Most people feel calmer after just 5 minutes of reframing.',
    },
    {
      title: 'You\'re Ready',
      content: 'Start with whatever is on your mind right now. There are no wrong answers—just honest reflection.',
      tip: 'Remember: If you\'re in crisis, please reach out to a professional or crisis line.',
    },
  ]

  const currentStep = steps[step]
  const isLastStep = step === steps.length - 1

  const handleNext = () => {
    if (isLastStep) {
      onComplete()
    } else {
      setStep(s => s + 1)
    }
  }

  const handleSkip = () => {
    onComplete()
  }

  return (
    <div className="onboarding-overlay">
      <div className="onboarding-modal">
        {/* Progress dots */}
        <div className="onboarding-progress">
          {steps.map((_, i) => (
            <div
              key={i}
              className={`onboarding-dot ${i === step ? 'active' : ''} ${i < step ? 'completed' : ''}`}
            />
          ))}
        </div>

        {/* Content */}
        <h2 className="onboarding-title">{currentStep.title}</h2>
        <p className="onboarding-content">{currentStep.content}</p>

        {/* Tip box */}
        <div className="onboarding-tip">
          <span className="onboarding-tip-icon">💡</span>
          <span>{currentStep.tip}</span>
        </div>

        {/* Actions */}
        <div className="onboarding-actions">
          {!isLastStep && (
            <button className="btn btn-text" onClick={handleSkip}>
              Skip intro
            </button>
          )}
          <button className="btn btn-primary" onClick={handleNext}>
            {isLastStep ? 'Get Started' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  )
}
